import React, { CSSProperties, useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import Select from "common/Select/Select.tsx";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { BotType, LanguageNameSpaces } from "@shared/types";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import { capitalizeFirstLetter } from "@shared/common/utils.ts";
import { KEYWORD_TO_SYMBOL_TITLE } from "@shared/features/bots/BotForm/common/constants.ts";
import useKeywordToSymbol from "@shared/features/bots/BotForm/General/useKeywordToSymbol.tsx";

interface KeywordToAlternativesProps {
  style?: CSSProperties;
}

const KeywordToSymbol = ({ style }: KeywordToAlternativesProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    subGroup,
    keywordToSymbolMapping,
    setKeywordToSymbolMapping,
    botType,
  } = useContext(BotFormContext)!;
  const { keywordToSymbolsMapping, errorMessage } = useKeywordToSymbol();
  const { currentData: groupData } =
    sharedLib.sharedApi.common.useGetGroupDataQuery(
      subGroup ? { sub_group_id: subGroup.id } : skipToken
    );
  const fieldTitle = t(KEYWORD_TO_SYMBOL_TITLE);

  const onChange = (keyword: string, symbol: string) => {
    setKeywordToSymbolMapping((keywordToSymbolMapping) => {
      return {
        ...keywordToSymbolMapping,
        [keyword]: symbol,
      };
    });
  };

  const getTitle = (keyword: string) =>
    capitalizeFirstLetter(keyword.toLowerCase());

  const getSymbolSelect = (keyword: string, alternativeAssets?: string[]) => {
    return (
      <Select
        style={{ width: "100%" }}
        value={keywordToSymbolMapping?.[keyword]}
        onChange={(symbol) => onChange(keyword, symbol)}
        optionLabelProp="label"
        placeholder="Choose a symbol"
        options={
          alternativeAssets?.map((option) => ({
            value: option,
            label: option,
          })) || []
        }
        mobileTitle={fieldTitle}
      />
    );
  };

  const getSymbolSection = (keyword: string) => {
    return (
      <div key={keyword}>
        <FormFieldTitle>{getTitle(keyword.toLowerCase())}:</FormFieldTitle>
        {getSymbolSelect(keyword, keywordToSymbolsMapping[keyword])}
      </div>
    );
  };

  if (
    !Object.keys(groupData?.keyword_to_symbols || {}).length ||
    botType !== BotType.SIGNAL
  )
    return null;

  return (
    <div style={style}>
      <FormFieldTitle>{fieldTitle}</FormFieldTitle>
      <EqualColumnsGrid numColumns={2}>
        {Object.keys(groupData?.keyword_to_symbols || {}).map(getSymbolSection)}
      </EqualColumnsGrid>
      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </div>
  );
};

export default KeywordToSymbol;
